import { QueryClient } from '@tanstack/react-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { clear, del, get, set } from 'idb-keyval'

/**
 * The browser's copy of the record, which is a cache and never the record.
 *
 * The distinction is the whole point. What is kept here is evictable, may be
 * stale, and can be thrown away at any moment without anybody losing work; the
 * account holds what is true. Before this the browser held the only copy, which
 * is what made a lost device a lost screening.
 *
 * What it buys: a screening on screen before the network answers. Signed in,
 * every load used to wait on a round trip, and the payroll panel waits on that
 * load before it dares fold a reading in.
 */

/**
 * IndexedDB rather than localStorage.
 *
 * localStorage is synchronous, so every read blocks the main thread, and it
 * caps around five megabytes of strings. A screening carries its full engine
 * output and a policy schedule read out of a PDF, and facts accumulate one row
 * per observation, so that ceiling is reachable rather than theoretical. It
 * also fails by throwing on write, which is the least useful moment to find out.
 */
const store = {
  getItem: (key: string) => get<string>(key).then((v) => v ?? null),
  setItem: (key: string, value: string) => set(key, value),
  removeItem: (key: string) => del(key),
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * Serve what is held, then check.
         *
         * `online` would leave a cached screening unrendered until the network
         * answered, which is the behaviour this is meant to remove.
         */
        networkMode: 'offlineFirst',
        /**
         * A minute, not zero.
         *
         * With `staleTime: 0` the list renders from cache and repaints the
         * moment the server answers, and in a timeline of dated screenings that
         * reads as a glitch. Nothing else writes this data, so what is held is
         * right until this app changes it, and it invalidates when it does.
         */
        staleTime: 60_000,
        retry: 1,
      },
      mutations: {
        networkMode: 'offlineFirst',
      },
    },
  })
}

/**
 * One cache per account, and nothing left behind on sign-out.
 *
 * Keyed by user because the alternative is one household's record sitting in the
 * next user's browser on a shared machine. Cleared as well as re-keyed, since a
 * key nobody reads is still a copy of somebody's household finances on disk.
 */
export function persisterFor(userId: string) {
  return createAsyncStoragePersister({
    storage: store,
    key: `asvise-home:cache:${userId}`,
    throttleTime: 1000,
  })
}

/** Everything this app has cached, gone. */
export async function forgetCache(): Promise<void> {
  try {
    await clear()
  } catch {
    // Storage denied or unavailable. There is nothing held to leak, then.
  }
}
